import React from 'react';
import { useStore, Competitor, ClientAttribute } from '../../store/useStore';
import { Input, Repeater, GalleryUpload, Textarea } from '../ui';
import { Crosshair } from 'lucide-react';

export function M6_Competitors() {
  const { competitors, setCompetitors } = useStore();

  const handleAdd = () => {
    setCompetitors([
      ...competitors,
      {
        id: Date.now().toString(),
        nome: '',
        site: '',
        instagram: '',
        pontosFortes: [],
        pontosFracos: []
      }
    ]);
  };

  const handleRemove = (index: number) => {
    setCompetitors(competitors.filter((_, i) => i !== index));
  };

  const handleChange = (id: string, field: keyof Competitor, value: any) => {
    setCompetitors(competitors.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const handleAddSubItem = (compId: string, field: 'pontosFortes' | 'pontosFracos') => {
    const novelItem: ClientAttribute = { id: crypto.randomUUID(), titulo: '', descricao: '' };
    setCompetitors(competitors.map(c => {
      if (c.id === compId) {
        return { ...c, [field]: [...c[field], novelItem] };
      }
      return c;
    }));
  };

  const handleUpdateSubItem = (compId: string, field: 'pontosFortes' | 'pontosFracos', itemIndex: number, key: keyof ClientAttribute, val: string) => {
    setCompetitors(competitors.map(c => {
      if (c.id === compId) {
        const updated = [...c[field]];
        updated[itemIndex] = { ...updated[itemIndex], [key]: val };
        return { ...c, [field]: updated };
      }
      return c;
    }));
  };

  const handleRemoveSubItem = (compId: string, field: 'pontosFortes' | 'pontosFracos', itemIndex: number) => {
    setCompetitors(competitors.map(c => {
      if (c.id === compId) {
        return { ...c, [field]: c[field].filter((_, i) => i !== itemIndex) };
      }
      return c;
    }));
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6">
        <Repeater
          title="Mapeamento de Concorrência"
          description="Liste os principais concorrentes diretos e indiretos do seu cliente."
          items={competitors}
          onAdd={handleAdd}
          onRemove={handleRemove}
          addButtonText="Adicionar Concorrente"
          getItemSummary={(item) => ({
            title: item.nome || 'Novo Concorrente',
            subtitle: item.instagram || item.site || 'Sem link informado'
          })}
          renderItem={(competitor) => (
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                <Crosshair size={18} className="text-red-500" />
                <h4 className="font-semibold text-gray-900">{competitor.nome || 'Novo Concorrente'}</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <Input 
                    label="Nome da Empresa" 
                    value={competitor.nome} 
                    onChange={(e) => handleChange(competitor.id, 'nome', e.target.value)} 
                    placeholder="Nome do concorrente" 
                 />
                 <Input 
                    label="Site" 
                    value={competitor.site} 
                    onChange={(e) => handleChange(competitor.id, 'site', e.target.value)} 
                    placeholder="https://" 
                 />
                 <Input 
                    label="Instagram" 
                    value={competitor.instagram} 
                    onChange={(e) => handleChange(competitor.id, 'instagram', e.target.value)} 
                    placeholder="@" 
                 />
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
                 <Repeater<ClientAttribute>
                    title="Pontos Fortes"
                    description="O que eles fazem bem?"
                    items={competitor.pontosFortes}
                    addButtonText="Novo Ponto Forte"
                    onAdd={() => handleAddSubItem(competitor.id, 'pontosFortes')}
                    onRemove={(idx) => handleRemoveSubItem(competitor.id, 'pontosFortes', idx)}
                    getItemSummary={(item) => ({ title: item.titulo || 'Novo Ponto', subtitle: item.descricao })}
                    renderItem={(item, index) => (
                      <div className="space-y-4">
                        <Input label="Qualidade Principal" value={item.titulo} onChange={(e) => handleUpdateSubItem(competitor.id, 'pontosFortes', index, 'titulo', e.target.value)} placeholder="Ex: Preço agressivo" />
                        <Textarea label="Detalhes" value={item.descricao} onChange={(e) => handleUpdateSubItem(competitor.id, 'pontosFortes', index, 'descricao', e.target.value)} placeholder="Como eles executam isso?" />
                      </div>
                    )}
                 />
                 <Repeater<ClientAttribute>
                    title="Pontos Fracos"
                    description="Onde pecam? Oportunidade?"
                    items={competitor.pontosFracos}
                    addButtonText="Novo Ponto Fraco"
                    onAdd={() => handleAddSubItem(competitor.id, 'pontosFracos')}
                    onRemove={(idx) => handleRemoveSubItem(competitor.id, 'pontosFracos', idx)}
                    getItemSummary={(item) => ({ title: item.titulo || 'Novo Ponto', subtitle: item.descricao })}
                    renderItem={(item, index) => (
                      <div className="space-y-4">
                        <Input label="Deficiência Principal" value={item.titulo} onChange={(e) => handleUpdateSubItem(competitor.id, 'pontosFracos', index, 'titulo', e.target.value)} placeholder="Ex: Atendimento lento" />
                        <Textarea label="Oportunidade p/ nós" value={item.descricao} onChange={(e) => handleUpdateSubItem(competitor.id, 'pontosFracos', index, 'descricao', e.target.value)} placeholder="Como podemos superar isso?" />
                      </div>
                    )}
                 />
              </div>

              <div className="pt-4 border-t border-gray-100">
                 <GalleryUpload 
                    label="Evidências (Dossiê Visual)"
                    description="Prints de anúncios, site, peças criativas, reclamações no ReclameAqui, etc."
                    onChange={(files) => handleChange(competitor.id, 'evidencias', files as any)}
                 />
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}
